import {PAYMENT_KEYS} from "@/api/paymentApi";
import CrossIcon from "@/assets/FF5A4F_cross_icon.svg?react";
import AdvancedIcon from "@/assets/pricing/7086FF_package_advanced.svg?react";
import EnterpriseIcon from "@/assets/pricing/7086FF_package_enterprise.svg?react";
import ExpertIcon from "@/assets/pricing/7086FF_package_expert.svg?react";
import ProfessionalIcon from "@/assets/pricing/7086FF_package_professional.svg?react";
import StarterIcon from "@/assets/pricing/7086FF_package_starter.svg?react";
import {usePaymentStore} from "@/store/payment";
import type {TCurrency, TPackageCategory, TPackageName, TSubscriptionWithDiscountOptions} from "@/store/types";
import {queryOptions, useQueries} from "@tanstack/react-query";
import type {AxiosError, AxiosResponse} from "axios";
import {map} from "lodash";
import {useEffect, useMemo, useState, type FC, type SVGProps} from "react";
import {useTranslation} from "react-i18next";
import {usePrevious} from "react-use";

export const packages: {
  [K in TPackageName]: {
    translationKey: string;
    icon: FC<SVGProps<SVGSVGElement>>;
    type: K;
  };
} = {
  NONE: {
    translationKey: "noActivePlan",
    icon: CrossIcon,
    type: "NONE",
  },
  STARTER: {
    translationKey: "packageStarter",
    icon: StarterIcon,
    type: "STARTER",
  },
  ESSENTIALS: {
    translationKey: "packageEssentials",
    icon: StarterIcon,
    type: "ESSENTIALS",
  },
  ADVANCED: {
    translationKey: "packageAdvanced",
    icon: AdvancedIcon,
    type: "ADVANCED",
  },
  PROFESSIONAL: {
    translationKey: "packageProfessional",
    icon: ProfessionalIcon,
    type: "PROFESSIONAL",
  },
  EXPERT: {
    translationKey: "packageExpert",
    icon: ExpertIcon,
    type: "EXPERT",
  },
  ENTERPRISE: {
    translationKey: "packageTailored",
    icon: EnterpriseIcon,
    type: "ENTERPRISE",
  },
} as const;

export const getPackageIcon = (packageType: keyof typeof packages, className: string = "") => {
  const pkg = packages[packageType];

  if (!pkg) return null;

  const PackageIcon = pkg.icon;
  return <PackageIcon className={className} />;
};
const usePackages = (currency: TCurrency, category?: TPackageCategory) => {
  const {t} = useTranslation();
  const setter = usePaymentStore(state => state.setter);
  const plans = useQueries({
    queries: (["INSTAGRAM", "TIKTOK", "BOTH"] as TPackageCategory[]).map(category =>
      queryOptions<AxiosResponse<TSubscriptionWithDiscountOptions[]>, AxiosError, TSubscriptionWithDiscountOptions[]>({
        queryKey: PAYMENT_KEYS.qPackageList(`currency=${currency}&package_category=${category}`),
        select: response => response.data,
      }),
    ),
  });
  const [isPending, setIsPending] = useState(true);
  const data = usePaymentStore(state => state.getPlans(category));

  const extendedData = useMemo(
    () =>
      data
        ? [
            ...data,
            {
              id: 5,
              name: "ENTERPRISE",
              influencers_per_campaign: "unlimited",
              invitations_per_campaign: "unlimited",
              influencer_level_insights: true,
              multiple_active_campaigns: true,
              other_countries: true,
              priority_notifications_and_advertising: true,
              personal_account_manager: true,
              price_per_month: 0,
              price_per_year: 0,
              currency: currency,
              package_type: "YEARLY",
              discount_percentage: 0,
              price_per_year_with_discount: 0,
              price_per_month_with_discount: 0,
              template_name: "",
              buyout: "custom",
            } as unknown as TSubscriptionWithDiscountOptions,
          ]
        : [],
    [data],
  );
  const extendedPackagesList = useMemo(() => map(packages, p => ({...p, name: t(p.translationKey)})), [t]);
  const packagesList = useMemo(() => extendedPackagesList.slice(1), [extendedPackagesList]);
  const previousPackages = usePrevious(data ?? []);
  const packagesData = useMemo(
    () => (previousPackages?.length && typeof data === "undefined" ? previousPackages : (data ?? [])),
    [data],
  );

  useEffect(() => {
    if (plans.every(plan => plan.isSuccess)) {
      setter(state => {
        state.instagram = plans[0].data!;
        state.tiktok = plans[1].data!;
        state.both = plans[2].data!;
      });

      setIsPending(false);
    }
  }, [plans, setter, setIsPending]);

  return {
    isPending,
    extendedPackagesList,
    packagesData,
    packagesList,
    packages,
    extendedData,
  };
};

export default usePackages;
